/**
 * Roadtrip Driving Mode — phone-in-cradle screen.
 *
 * Optimised for CarPlay/Android Auto browser mirror + tap-once
 * interactions. Big fonts, dark background, single call-to-action.
 * Restores state from ?trip=<id> or the last-planned trip cached in
 * localStorage. One giant button lets the driver update remaining km
 * at any stop and re-plan without leaving the app.
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Zap, MapPin, ArrowLeft, Navigation, Volume2, VolumeX, ChevronUp, ChevronDown } from "lucide-react";

// Global check-in / SMS-group / TripMap snippets — the SAME components
// that ship on HustleFinder listing pages. See /app/memory/AGENT_RULES
// modular-snippet rule: never re-implement these.
import CheckinButton   from "@/components/CheckinButton";
import SmsSubscribers  from "@/components/SmsSubscribers";
import TripMap         from "@/components/TripMap";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return "—"; }
}

function fmtDur(minutes) {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h} h` : `${h} h ${rem} min`;
}

function fmtDist(metres) {
  const m = Math.max(0, Math.round(Number(metres) || 0));
  if (m < 1000) return `${m} m`;
  const km = m / 1000;
  return `${km < 10 ? km.toFixed(1) : Math.round(km)} km`;
}

export default function RoadtripDrivePage() {
  const [params] = useSearchParams();
  const [plan, setPlan]  = useState(null);
  const [err, setErr]    = useState(null);
  const [showUpdate, setShowUpdate] = useState(false);
  const [remKm, setRemKm] = useState("");
  const [showSms, setShowSms] = useState(false);

  // Turn-by-turn state
  const [steps, setSteps]   = useState(null);   // {legs, distance_km, duration_s}
  const [stepsErr, setStepsErr] = useState(null);
  const [ttIdx, setTtIdx]   = useState(0);       // current step index (flat)
  const [ttOpen, setTtOpen] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const [gpsPos, setGpsPos] = useState(null);
  const gpsWatchIdRef = useRef(null);
  const lastSpokenRef = useRef(-1);

  // Rebuild the last plan from URL params (which we also save to
  // localStorage on the main planner). Any subsequent Update-km replan
  // updates localStorage so returning to this page always shows the
  // current itinerary.
  const [qs, setQs] = useState(() => {
    try {
      const cached = window.localStorage.getItem("hs-roadtrip-last-qs");
      return cached || params.toString();
    } catch { return params.toString(); }
  });

  async function runPlan(overrideQs) {
    setErr(null);
    try {
      const use = overrideQs || qs;
      if (!use) return;
      const r = await fetch(`${API}/api/roadtrip/plan?${use}`);
      if (!r.ok) throw new Error((await r.json()).detail || "Plan failed");
      const d = await r.json();
      setPlan(d);
      try { window.localStorage.setItem("hs-roadtrip-last-qs", use); } catch { /* noop */ }
    } catch (e) { setErr(e.message); }
  }

  useEffect(() => { runPlan(); /* eslint-disable-line */ }, []);

  // ── Check-in trail (breadcrumb) — shared /api/checkin/trail
  // endpoint powers this AND the HustleFinder page. Key = trip id
  // from ?trip= or, if none, a stable hash of the qs (same trip
  // definition → same key). Never falls back to random.
  const tripKey = useMemo(() => {
    const t = params.get("trip") || params.get("trip_id");
    if (t) return `trip:${t}`;
    if (qs) return `qs:${qs}`;
    return "";
  }, [params, qs]);

  const [trail, setTrail] = useState([]);
  const loadTrail = async () => {
    if (!tripKey) return;
    try {
      const r = await fetch(
        `${API}/api/checkin/trail?source=roadtrip`
        + `&key=${encodeURIComponent(tripKey)}`);
      const j = await r.json();
      setTrail(j.points || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadTrail(); }, [tripKey]);

  // Build the TripMap `stops[]` from the plan.timeline (charger vs
  // lodging vs generic detected by name / sub_niche).
  const mapStops = useMemo(() => {
    const rows = plan?.timeline || [];
    return rows
      .filter((s) => typeof s.lat === "number" && typeof s.lng === "number")
      .map((s) => {
        const n = (s.name || "").toLowerCase();
        const nc = (s.sub_niche || "").toLowerCase();
        let kind = "stop";
        if (/charg|tesla|super\s*charg|ev\b/.test(n + nc)) kind = "charger";
        else if (/fuel|petrol|servo|caltex|shell|bp|7[-\s]?eleven/
                    .test(n + nc)) kind = "fuel";
        return {
          lat:   s.lat,
          lng:   s.lng,
          label: s.name || "Stop",
          kind,
          subtitle: s.suburb
             ? `${s.suburb}${s.state_code ? ", " + s.state_code : ""}`
             : (s.address || ""),
        };
      });
  }, [plan]);

  // Find the next stop = first timeline item whose arrival_iso is in
  // the future. If no schedule, first item.
  const nextStop = useMemo(() => {
    if (!plan?.timeline?.length) return null;
    const now = Date.now();
    const upcoming = plan.timeline.find((s) => {
      if (!s.arrival_iso) return true;
      return new Date(s.arrival_iso).getTime() > now;
    });
    return upcoming || plan.timeline[plan.timeline.length - 1];
  }, [plan]);

  const kmToNext = nextStop
    ? Math.max(0, (nextStop.km_from_origin || 0)).toFixed(0)
    : "—";

  const applyUpdate = () => {
    const rk = Number(remKm);
    if (!rk || rk < 20) { setErr("Enter remaining km ≥ 20."); return; }
    const params = new URLSearchParams(qs);
    params.set("remaining_km", String(rk));
    const newQs = params.toString();
    setQs(newQs);
    setShowUpdate(false); setRemKm("");
    runPlan(newQs);
  };

  // Flatten legs → single steps[] array for progress tracking.
  const flatSteps = useMemo(() => {
    if (!steps?.legs) return [];
    return steps.legs.flatMap(l => l.steps || []);
  }, [steps]);

  // Fetch turn-by-turn instructions from current position → next stop.
  async function fetchSteps(fromLat, fromLng, toLat, toLng) {
    setStepsErr(null); setSteps(null); setTtIdx(0);
    lastSpokenRef.current = -1;
    try {
      const url = `${API}/api/roadtrip/steps?from_lat=${fromLat}&from_lng=${fromLng}` +
                    `&to_lat=${toLat}&to_lng=${toLng}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      setSteps(await r.json());
    } catch (e) { setStepsErr(e.message); }
  }

  // On next-stop change, if we have GPS OR a plan origin, fetch new steps.
  useEffect(() => {
    if (!nextStop || !ttOpen) return;
    const toLat = nextStop.lat ?? nextStop.latitude;
    const toLng = nextStop.lng ?? nextStop.longitude;
    if (typeof toLat !== "number" || typeof toLng !== "number") return;
    const fromLat = gpsPos?.lat ?? plan?.origin?.lat;
    const fromLng = gpsPos?.lng ?? plan?.origin?.lng;
    if (typeof fromLat === "number" && typeof fromLng === "number") {
      fetchSteps(fromLat, fromLng, toLat, toLng);
    }
  }, [nextStop?.name, ttOpen]);

  // Live GPS watch — advances ttIdx when driver crosses each maneuver point.
  useEffect(() => {
    if (!ttOpen || !navigator?.geolocation) return;
    gpsWatchIdRef.current = navigator.geolocation.watchPosition(
      (p) => {
        setGpsPos({ lat: p.coords.latitude, lng: p.coords.longitude });
      },
      () => { /* ignore */ },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    return () => {
      if (gpsWatchIdRef.current !== null && navigator?.geolocation) {
        navigator.geolocation.clearWatch(gpsWatchIdRef.current);
      }
    };
  }, [ttOpen]);

  // Auto-advance ttIdx as we cross the next step's manoeuvre point.
  useEffect(() => {
    if (!gpsPos || !flatSteps.length) return;
    // Find the closest upcoming step; if within 25m, advance.
    for (let i = ttIdx; i < flatSteps.length; i++) {
      const loc = flatSteps[i]?.location;
      if (!Array.isArray(loc) || loc.length !== 2) continue;
      const [lng, lat] = loc;
      const dLat = (lat - gpsPos.lat) * 111000;
      const dLng = (lng - gpsPos.lng) * 111000 *
                        Math.cos(lat * Math.PI / 180);
      const dist = Math.sqrt(dLat*dLat + dLng*dLng);
      if (dist < 25) {
        if (i > ttIdx) setTtIdx(i);
        break;
      }
    }
  }, [gpsPos, flatSteps, ttIdx]);

  // Speak the current instruction whenever ttIdx changes and voice is on.
  useEffect(() => {
    if (!voiceOn || !flatSteps.length || ttIdx === lastSpokenRef.current) return;
    const s = flatSteps[ttIdx];
    if (!s || typeof window.speechSynthesis === "undefined") return;
    try {
      const u = new SpeechSynthesisUtterance(
        `${s.instruction}. In ${fmtDist(s.distance_m)}.`);
      u.rate = 1.0; u.pitch = 1.0; u.lang = "en-AU";
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
      lastSpokenRef.current = ttIdx;
    } catch { /* noop */ }
  }, [ttIdx, voiceOn, flatSteps]);

  function toggleVoice() {
    const next = !voiceOn;
    setVoiceOn(next);
    if (!next && typeof window.speechSynthesis !== "undefined") {
      window.speechSynthesis.cancel();
    } else if (next) {
      // Speak the current step immediately to prime iOS audio unlock.
      lastSpokenRef.current = -1;
      const s = flatSteps[ttIdx];
      if (s && typeof window.speechSynthesis !== "undefined") {
        try {
          const u = new SpeechSynthesisUtterance(
            `Turn-by-turn on. ${s.instruction}. In ${fmtDist(s.distance_m)}.`);
          u.lang = "en-AU"; window.speechSynthesis.speak(u);
          lastSpokenRef.current = ttIdx;
        } catch { /* noop */ }
      }
    }
  }

  const currentStep = flatSteps[ttIdx];
  const nextStepPrev = flatSteps[ttIdx + 1];

  return (
    <div
      data-testid="roadtrip-drive-page"
      className="min-h-screen bg-black text-white flex flex-col"
      style={{ WebkitTapHighlightColor: "transparent" }}
    >
      <header className="px-4 py-3 flex items-center justify-between border-b border-neutral-800">
        <Link
          to="/roadtrip"
          data-testid="drive-back"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Exit driving mode
        </Link>
        {plan?.schedule?.arrive_at && (
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-neutral-500">
              Arrive destination
            </p>
            <p className="text-lg font-bold" data-testid="drive-eta-final">
              {fmtDT(plan.schedule.arrive_at)}
            </p>
          </div>
        )}
      </header>

      <main className="flex-1 px-6 py-8 flex flex-col justify-between">
        {err && (
          <p className="text-red-400 text-lg mb-6">{err}</p>
        )}

        {plan && nextStop ? (
          <>
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-neutral-500 mb-3">
                Next stop
              </p>
              <p className="text-4xl md:text-6xl font-extrabold leading-tight mb-1"
                  data-testid="drive-next-name">
                {nextStop.name}
              </p>
              <p className="text-xl md:text-2xl text-neutral-400 mb-6"
                  data-testid="drive-next-location">
                {nextStop.friendly_location || nextStop.suburb || "—"}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="rounded-2xl bg-neutral-900 p-5">
                  <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                    Distance
                  </p>
                  <p className="text-4xl font-extrabold" data-testid="drive-km-next">
                    {kmToNext}<span className="text-lg text-neutral-500 ml-1">km</span>
                  </p>
                </div>
                <div className="rounded-2xl bg-neutral-900 p-5">
                  <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                    Arrive
                  </p>
                  <p className="text-4xl font-extrabold" data-testid="drive-eta-next">
                    {nextStop.arrival_iso
                      ? new Date(nextStop.arrival_iso).toLocaleTimeString("en-AU",
                          { hour: "2-digit", minute: "2-digit", hour12: false })
                      : "—"}
                  </p>
                </div>
              </div>

              {nextStop.kind === "ev_charging" && (
                <p className="inline-flex items-center gap-2 text-amber-400 text-lg mb-6"
                    data-testid="drive-charger-info">
                  <Zap className="w-5 h-5" />
                  {nextStop.fast_label} · {fmtDur(nextStop.dwell_min)} charge
                </p>
              )}
              {nextStop.kind === "sleepover" && (
                <p className="inline-flex items-center gap-2 text-purple-400 text-lg mb-6"
                    data-testid="drive-hotel-info">
                  🛏 Overnight · check-in {fmtDT(nextStop.checkin_iso)}
                </p>
              )}
            </div>

            {/* Route map + live check-in (shared TripMap snippet). Same
                component powers /roadtrip planner AND HustleFinder. */}
            <div className="mt-6 rounded-2xl overflow-hidden border border-neutral-800"
                  data-testid="drive-tripmap-wrapper">
              <TripMap
                mode="combined"
                stops={mapStops}
                trail={trail}
                liveLocation={gpsPos
                  ? { lat: gpsPos.lat, lng: gpsPos.lng, title: "You are here" }
                  : null}
                height={280}
                testid="drive-tripmap"
              />
            </div>
            {tripKey && (
              <div className="mt-4 flex items-center justify-center gap-3"
                    data-testid="drive-checkin-wrap">
                <CheckinButton
                  source="roadtrip"
                  targetKey={tripKey}
                  label="I'm here — text my group"
                  onSuccess={loadTrail}
                  testid="drive-checkin"
                />
                <button
                  type="button"
                  data-testid="drive-manage-sms"
                  onClick={() => setShowSms((s) => !s)}
                  className="text-neutral-400 hover:text-white text-sm underline underline-offset-4"
                >SMS group…</button>
              </div>
            )}
            {showSms && tripKey && (
              <div className="mt-4" data-testid="drive-sms-panel">
                <SmsSubscribers source="roadtrip"
                                 targetKey={tripKey}
                                 testid="drive-sms" />
              </div>
            )}

            <button
              onClick={() => setShowUpdate(true)}
              data-testid="drive-update-btn"
              className="w-full py-6 rounded-3xl bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white text-2xl font-bold shadow-lg mt-6"
            >
              📍 Update remaining km &amp; replan
            </button>
            <p className="text-center text-neutral-500 text-xs mt-3">
              Tap after any side-trip (beach, café, lookout) so the app can
              recalculate the next charger against your real battery.
            </p>

            {/* Turn-by-turn — collapsible drawer */}
            <button
              onClick={() => setTtOpen(o => !o)}
              data-testid="drive-tt-toggle"
              className="w-full mt-6 py-4 rounded-2xl bg-neutral-900 border border-neutral-800 hover:border-neutral-700 flex items-center justify-between px-5"
            >
              <span className="inline-flex items-center gap-3 text-white">
                <Navigation className="w-5 h-5" strokeWidth={1.6} />
                <span className="text-lg font-semibold">Turn-by-turn</span>
                {steps && (
                  <span className="text-neutral-500 text-sm">
                    · {fmtDist(steps.distance_km * 1000)} · {fmtDur(steps.duration_s / 60)}
                  </span>
                )}
              </span>
              {ttOpen
                ? <ChevronDown className="w-5 h-5 text-neutral-400" />
                : <ChevronUp className="w-5 h-5 text-neutral-400" />}
            </button>

            {ttOpen && (
              <div
                data-testid="drive-tt-panel"
                className="mt-3 rounded-2xl bg-neutral-950 border border-neutral-800 p-4"
              >
                {/* Current + peek-next */}
                {currentStep ? (
                  <>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1">
                        <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                          Now
                        </p>
                        <p className="text-2xl md:text-3xl font-bold leading-tight"
                            data-testid="drive-tt-current">
                          {currentStep.instruction}
                        </p>
                        <p className="text-neutral-400 text-sm mt-1">
                          In {fmtDist(currentStep.distance_m)} ·{" "}
                          {fmtDur(currentStep.duration_s / 60)}
                        </p>
                      </div>
                      <button
                        onClick={toggleVoice}
                        data-testid="drive-tt-voice"
                        aria-pressed={voiceOn}
                        title={voiceOn ? "Mute voice" : "Speak instructions"}
                        className={`rounded-2xl p-3 border ${
                          voiceOn
                            ? "bg-emerald-600 border-emerald-500"
                            : "bg-neutral-900 border-neutral-700 hover:border-neutral-500"
                        }`}
                      >
                        {voiceOn
                          ? <Volume2 className="w-6 h-6 text-white" />
                          : <VolumeX className="w-6 h-6 text-neutral-400" />}
                      </button>
                    </div>
                    {nextStepPrev && (
                      <div className="text-neutral-500 text-sm border-t border-neutral-800 pt-3"
                            data-testid="drive-tt-next">
                        Then: {nextStepPrev.instruction}
                        <span className="text-neutral-600 ml-2">
                          ({fmtDist(nextStepPrev.distance_m)})
                        </span>
                      </div>
                    )}
                    {/* Full scrolling list */}
                    <div className="mt-4 max-h-64 overflow-y-auto -mx-1 px-1">
                      <ul className="space-y-1">
                        {flatSteps.map((s, i) => (
                          <li
                            key={i}
                            data-testid={`drive-tt-step-${i}`}
                            onClick={() => setTtIdx(i)}
                            className={`px-3 py-2 rounded-lg cursor-pointer ${
                              i === ttIdx ? "bg-emerald-950/50 border border-emerald-700"
                                            : "hover:bg-neutral-900 border border-transparent"
                            }`}
                          >
                            <p className={`text-sm ${
                              i === ttIdx ? "text-emerald-200" : "text-neutral-300"
                            }`}>
                              {i + 1}. {s.instruction}
                            </p>
                            <p className="text-xs text-neutral-500">
                              {fmtDist(s.distance_m)} · {fmtDur(s.duration_s / 60)}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                ) : stepsErr ? (
                  <p className="text-red-400 text-sm" data-testid="drive-tt-error">
                    Could not fetch directions: {stepsErr}
                  </p>
                ) : (
                  <p className="text-neutral-500 text-sm">
                    Loading turn-by-turn from your current location…
                    {!navigator?.geolocation && (
                      <span className="block mt-1 text-xs">
                        Geolocation unavailable — using trip origin as start point.
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}
          </>
        ) : !err ? (
          <p className="text-neutral-500 text-lg">Loading last trip…</p>
        ) : null}
      </main>

      {/* Update remaining-km sheet */}
      {showUpdate && (
        <div
          data-testid="drive-update-modal"
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center px-4"
          onClick={() => setShowUpdate(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-neutral-900 rounded-3xl p-8 w-full max-w-md"
          >
            <p className="text-sm uppercase tracking-widest text-neutral-500 mb-1">
              Remaining range
            </p>
            <p className="text-neutral-300 mb-6 text-lg">
              What does your car dash show right now?
            </p>
            <input
              data-testid="drive-remaining-km-input"
              type="number"
              inputMode="numeric"
              min={20} max={1000}
              value={remKm}
              onChange={(e) => setRemKm(e.target.value)}
              placeholder="e.g. 260"
              autoFocus
              className="w-full px-4 py-5 rounded-2xl bg-black border border-neutral-700 text-white text-4xl font-bold text-center mb-6"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpdate(false)}
                className="flex-1 py-4 rounded-2xl border border-neutral-700 text-white text-lg"
              >
                Cancel
              </button>
              <button
                data-testid="drive-update-apply"
                onClick={applyUpdate}
                className="flex-1 py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-lg"
              >
                Replan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
